import { useEffect, useState } from "react";
import { CalcRequestValue } from "../../../../../application/domain/payment/calc-request";
import { useDebouncedCallback } from "use-debounce";
import apiClient from "../../../../../application/apis/api-client";
import {
  SmartApplyApiError,
  SmartApplyErrorCodeLiterals,
} from "@graffer-inc/smart-apply";
import { Payment } from "@graffer-inc/cloud-gov-core";

export type PaymentCalculateResult = {
  calcTarget: null | CalcRequestValue;
  payment: null | Payment;
};

export type PaymentCalcStateLiterals = "success" | "error" | "refreshing";

export type PaymentCalcState = {
  step: PaymentCalcStateLiterals;
  errorCode?: Extract<
    SmartApplyErrorCodeLiterals,
    "bad_request.payment_amount_is_less_than_minimum"
  >;
};

export function useRequestPayment() {
  const [calcRequest, setCalcRequest] = useState<CalcRequestValue>(
    CalcRequestValue.initial(),
  );
  const [paymentResult, setPaymentResult] = useState<PaymentCalculateResult>({
    calcTarget: null,
    payment: null,
  });

  const [paymentCalcState, setPaymentCalcState] = useState<PaymentCalcState>({
    step: "error",
  });

  const debounced = useDebouncedCallback(
    // function
    async (req: CalcRequestValue) => {
      try {
        const { data } = await apiClient.calcPayment(req.getValue());
        setPaymentResult({
          calcTarget: req,
          payment: data,
        });
        setPaymentCalcState({ step: "success" });
      } catch (e) {
        const apiError = new SmartApplyApiError(e);
        await apiError.resolveErrorResponse();
        if (SmartApplyApiError.isRetryable(e)) {
          const { data } = await apiClient.calcPayment(req.getValue());
          setPaymentResult({
            calcTarget: req,
            payment: data,
          });
          return;
        }
        if (
          SmartApplyApiError.isErrorCode(
            apiError,
            "bad_request.payment_amount_is_less_than_minimum",
          )
        ) {
          setPaymentCalcState({
            step: "error",
            errorCode: "bad_request.payment_amount_is_less_than_minimum",
          });
          return;
        }
        throw e;
      }
    },
    // delay in ms
    1000,
  );

  useEffect(() => {
    (() => {
      if (!calcRequest.isValid()) {
        setPaymentCalcState({
          step: "error",
        });
        return;
      }

      setPaymentCalcState({
        step: "refreshing",
      });
      debounced(calcRequest);
    })();
  }, [calcRequest, debounced, setPaymentCalcState]);

  return {
    calcRequest,
    setCalcRequest,
    paymentResult,
    paymentCalcState,
  };
}
